<template lang="pug">
v-dialog(v-model='isDialogVisible' :max-width='maxWidth' persistent)
    template(v-slot:activator='{ props }')
        slot(name='activator' v-bind='props')
            IconButton(
                v-bind='props'
                :icon='createNew ? "mdi-plus" : "mdi-pencil"'
                :color='iconColor'
            )

    // This seems to validate on blur, regardless of the value of validateOn, I'm not sure why.
    v-form(v-model='valid' @submit.prevent :validate-on='validateOn' ref='form')
        v-card
            v-card-title {{ title }}
            v-card-subtitle {{ subTitle }}
            v-card-text
                slot(:draftValue='innerValue')
            p.error(v-if='showManualValidationMessage && manualValidationTrigger.isValid == false') Error: {{ manualValidationTrigger.message }}
            v-card-actions
                v-spacer
                ButtonExit(@click='cancelled') Cancel
                ButtonSubmit(type='submit' @click='saveClicked' :disabled='isDisabled')
</template>

<script lang="ts">
interface ManualValidationTriggerDTO {
    isValid: boolean;
    message: string;
}

import { defineComponent, PropType } from 'vue';
import { alertException } from '@/util/errors';
import { IconButton } from '@/common/';
import ButtonSubmit from '@/common/button-components/ButtonSubmit.vue';
import { deepCloneRawIfValid } from '@/util/utilsMisc';
import ButtonExit from '@/common/button-components/ButtonExit.vue';

export default defineComponent({
    name: 'BaseCreateEditDialog',
    components: {
        ButtonExit,
        ButtonSubmit,
        IconButton,
    },
    emits: ['saveSuccessful', 'cancelled'],
    inheritAttrs: false,
    props: {
        title: {
            type: String,
            required: true,
        },
        subTitle: {
            type: String,
            default: '',
        },
        // Whether this component is being used to create a new object.
        // Otherwise, assume it's for editing
        createNew: {
            type: Boolean,
            default: false,
        },
        dataCy: {
            type: String,
            default: '',
        },
        value: {
            type: Object,
            default: null,
        },
        // A callback to perform the API call on save
        actionOnSave: {
            type: Function,
            default: null,
        },
        saveCallbackFailedMessage: {
            type: String,
            default: 'Failed to save',
        },
        maxWidth: {
            type: String,
            default: '500px',
        },
        iconColor: {
            type: String,
            default: 'primary',
        },
        validateOnOverride: {
            // Values like 'input' or 'blur', but generally leave as null for default behaviour.
            type: String,
            default: null,
        },

        manualValidationTrigger: {
            type: Object as PropType<ManualValidationTriggerDTO>,
            default: null,
        },
    },
    data() {
        return {
            isDialogVisible: false,
            innerValue: null,
            isSaving: false,
            canSave: false,
            valid: false,
            haveValidated: false,
            showManualValidationMessage: false,
        };
    },
    computed: {
        isDisabled(): boolean {
            if (this.manualValidationTrigger) {
                return !this.manualValidationTrigger.isValid && this.haveValidated;
            }
            return !this.valid && this.haveValidated;
        },
        validateOn() {
            if (this.validateOnOverride) {
                return this.validateOnOverride;
            }

            // If invalid, from then on we want the input fields to become valid as soon as their input is correct,
            // rather than just when it blurs.
            return this.haveValidated ? 'input' : 'blur';
        },
    },
    watch: {
        value: function () {
            this.innerValue = deepCloneRawIfValid(this.value);
        },
    },
    mounted: function () {
        this.showManualValidationMessage = false;
        if (this.value) {
            this.innerValue = deepCloneRawIfValid(this.value);
        }
    },
    methods: {
        async saveClicked(): Promise<void> {
            const isValid = await this.isValid();
            if (!isValid) {
                return;
            }
            await this.handleSave();
        },

        async isValid(): Promise<boolean> {
            if (this.manualValidationTrigger) {
                return this.validateManually();
            } else {
                return await this.validateForm();
            }
        },

        validateManually(): boolean {
            this.valid = this.manualValidationTrigger.isValid;
            if (!this.valid) {
                this.showManualValidationMessage = true;
                console.error('Manual validation failed', this.manualValidationTrigger.message);
                return false;
            }
            return true;
        },

        async validateForm(): Promise<boolean> {
            const { valid } = await this.$refs.form.validate();
            this.haveValidated = true;

            if (this.isFormEmpty()) {
                // TODO: Handle gracefully
                console.error('Cannot save: The form is currently empty');
                return false;
            }
            if (!valid) {
                // TODO: Handle gracefully
                return false;
            }
            return true;
        },

        isFormEmpty(): boolean {
            const hasDetectedInputsInsideForm = this.$refs.form.items.length > 0;
            return !hasDetectedInputsInsideForm;
        },

        async handleSave(): Promise<void> {
            this.isSaving = true;

            try {
                if (this.actionOnSave) {
                    await this.actionOnSave(this.innerValue);
                }
                this.$emit('saveSuccessful', this.isDialogVisible);
            } catch (e) {
                alertException(e, this.saveCallbackFailedMessage);
            } finally {
                this.reset();
            }
        },
        async cancelled(): Promise<void> {
            this.$emit('cancelled');
            this.reset();
        },
        reset(): void {
            this.isDialogVisible = false;
            this.isSaving = false;

            // Reset dialog value
            if (this.value === null) {
                this.innerValue = null;
            } else {
                this.innerValue = deepCloneRawIfValid(this.value);
            }
            this.haveValidated = false;
            this.showManualValidationMessage = false;
        },
    },
});
</script>
<style lang="scss" scoped>
.error {
    color: red;
    font-size: 0.8em;
    margin: auto;
}
</style>
